import React from 'react';
import { motion } from 'framer-motion';
import { Check, Shield, Clock, ArrowLeft, Moon, Sun } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCurrencyInfo } from '../../lib/regions';
import { useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe outside of component to avoid recreating object on every render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PricingPage = ({ isDarkMode, setIsDarkMode }) => {
    const { profile, user } = useAuth();
    const navigate = useNavigate();
    const [detectedRegion, setDetectedRegion] = React.useState(profile?.pricing_region || 'Asia');
    const currency = getCurrencyInfo(detectedRegion);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        const getRegion = async () => {
            if (profile?.pricing_region) {
                setDetectedRegion(profile.pricing_region);
                return;
            }
            const region = await import('../../lib/regions').then(m => m.detectRegion());
            setDetectedRegion(region);
        };
        getRegion();
    }, [profile?.pricing_region]);

    const handleSubscription = async (priceId) => {
        if (!user) {
            navigate('/signup');
            return;
        }

        // Safety check for placeholders
        if (priceId && priceId.includes('REPLACE_WITH')) {
            alert("Pricing is not yet configured. Please add the actual Stripe Price ID in src/lib/regions.js");
            return;
        }

        console.log('Initiating subscription for priceId:', priceId);
        setLoading(true);

        try {
            const stripe = await stripePromise;
            if (!stripe) throw new Error('Stripe failed to load');

            // Call the backend API to create the checkout session
            const response = await fetch('/api/create-checkout-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    priceId: priceId,
                    userId: user.id,
                    userEmail: user.email,
                    successUrl: `${window.location.origin}/success`,
                    cancelUrl: `${window.location.origin}/pricing?payment=cancelled`,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Server responded with ${response.status}`);
            }

            const session = await response.json();

            if (!session.sessionId) {
                throw new Error("No sessionId returned from server.");
            }

            if (!session.url) {
                throw new Error("No checkout URL returned from server.");
            }

            // Redirect to Stripe Checkout using the URL returned by the server
            window.location.href = session.url;

        } catch (error) {
            console.error('Payment Error:', error);
            alert(`Payment Error: ${error.message}`);
            setLoading(false);
        }
    };

    const plans = [
        {
            name: 'Lunar Access (Free)',
            price: '0',
            duration: '2 Months',
            description: 'Begin your journey with the cooling wisdom of Ida.',
            features: [
                'Complete access to all charts',
                'Real-time nostril detection',
                'Solar & Lunar timings',
                'Basic Tithi calculations',
                '2 Months of unlimited practice',
            ],
            cta: 'Start Practice',
            popular: false,
            color: 'blue',
            icon: Moon
        },
        {
            name: 'Solar Mastery (Premium)',
            price: currency.price,
            symbol: currency.symbol,
            duration: 'Monthly',
            description: 'Achieve full vitality with the burning fire of Pingala.',
            features: [
                'Everything in Lunar Access',
                'Unlimited lifetime duration',
                'Priority rhythm notifications',
                'Advanced planetary vedhas',
                'Exportable breath logs',
            ],
            cta: 'Achieve Mastery',
            popular: true,
            color: 'orange',
            icon: Sun
        }
    ];

    return (
        <div className={`min-h-screen transition-colors duration-500 py-20 px-4 relative overflow-hidden font-sans ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-[#FCFDFE] text-slate-900'
            }`}>
            {/* Background Orbs */}
            {isDarkMode ? (
                <>
                    <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-600/5 rounded-full blur-[150px] pointer-events-none" />
                    <div className="absolute bottom-[-10%] left-[-10%] w-[60%] h-[60%] bg-purple-600/5 rounded-full blur-[150px] pointer-events-none" />
                </>
            ) : (
                <>
                    <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[60%] bg-blue-400/5 rounded-full blur-[150px] pointer-events-none" />
                    <div className="absolute bottom-[-20%] left-[-10%] w-[60%] h-[60%] bg-orange-500/5 rounded-full blur-[150px] pointer-events-none" />
                </>
            )}

            {/* Back Navigation & Theme Toggle */}
            <div className="absolute top-8 left-8 right-8 flex justify-between items-center z-20">
                <div className="flex flex-col">
                    <Link
                        to="/"
                        className={`group flex items-center space-x-3 transition-all duration-300 ${isDarkMode ? 'text-gray-500 hover:text-white' : 'text-slate-400 hover:text-slate-900'}`}
                    >
                        <div className={`w-11 h-11 border rounded-[14px] flex items-center justify-center transition-all shadow-sm ${isDarkMode ? 'bg-white/5 border-white/10 group-hover:bg-white/10' : 'bg-white border-slate-200 group-hover:bg-slate-50'
                            }`}>
                            <ArrowLeft className="w-5 h-5" />
                        </div>
                        <span className="font-black text-xs uppercase tracking-widest">Back</span>
                    </Link>
                    <span className={`text-[9px] mt-2 font-black uppercase tracking-widest italic ${isDarkMode ? 'text-gray-600' : 'text-slate-300'}`}>
                        Region: {detectedRegion} ({currency.code})
                    </span>
                </div>

                <button
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    className={`p-2.5 rounded-xl transition-all shadow-sm ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-yellow-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                        }`}
                >
                    {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
            </div>

            <div className="max-w-6xl mx-auto relative z-10">
                <div className="text-center mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`text-5xl font-black mb-4 uppercase tracking-tighter ${isDarkMode ? 'text-white' : 'bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent'
                            }`}
                    >
                        Choose Your Path
                    </motion.h2>
                    <p className={`text-xl font-medium max-w-2xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                        Whether you seek cooling peace or heating power, we have a plan for you. Detected region: <span className="text-blue-500 font-bold">{detectedRegion}</span>
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                    {plans.map((plan, idx) => {
                        const Icon = plan.icon;
                        const isBlue = plan.color === 'blue';

                        return (
                            <motion.div
                                key={plan.name}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: idx * 0.1 }}
                                className={`relative rounded-[40px] p-10 border-2 transition-all duration-500 ${isDarkMode
                                    ? 'bg-[#111111]/80 backdrop-blur-xl border-white/[0.08] shadow-2xl'
                                    : (plan.popular
                                        ? 'bg-white border-orange-100 shadow-[0_30px_60px_-15px_rgba(249,115,22,0.15)]'
                                        : 'bg-white border-blue-100 shadow-[0_30px_60px_-15px_rgba(59,130,246,0.1)]')
                                    }`}
                            >
                                {plan.popular && (
                                    <div className="absolute top-0 right-10 transform -translate-y-1/2 bg-orange-500 text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">
                                        Surya Path
                                    </div>
                                )}
                                {!plan.popular && (
                                    <div className="absolute top-0 left-10 transform -translate-y-1/2 bg-blue-500 text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">
                                        Chandra Path
                                    </div>
                                )}

                                <div className="mb-10 flex items-center space-x-4">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isBlue ? 'bg-blue-500/10 text-blue-500' : 'bg-orange-500/10 text-orange-500'}`}>
                                        <Icon className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <h3 className={`text-2xl font-black uppercase tracking-tighter ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{plan.name}</h3>
                                        <p className={`text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-slate-500'}`}>{plan.description}</p>
                                    </div>
                                </div>

                                <div className={`flex items-baseline mb-10 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                    <span className="text-6xl font-black tracking-tighter">{plan.symbol || ''}{plan.price}</span>
                                    <span className={`ml-3 font-bold uppercase tracking-widest text-sm ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`}>/ {plan.duration}</span>
                                </div>

                                <div className="space-y-5 mb-12">
                                    {plan.features.map((feature, fIdx) => (
                                        <div key={fIdx} className="flex items-center space-x-4">
                                            <div className={`p-1 rounded-full ${isBlue ? 'bg-blue-500/20 text-blue-500' : 'bg-orange-500/20 text-orange-500'}`}>
                                                <Check className="w-4 h-4" />
                                            </div>
                                            <span className={`font-bold text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-slate-600'}`}>{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={() => {
                                        if (!plan.popular) {
                                            // Free plan logic if needed, or just redirect to dashboard
                                            navigate('/dashboard');
                                        } else {
                                            handleSubscription(currency.stripePriceId);
                                        }
                                    }}
                                    disabled={loading}
                                    className={`w-full py-5 rounded-[24px] font-black text-lg transition-all transform active:scale-[0.98] ${plan.popular
                                        ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-xl shadow-orange-500/30'
                                        : 'bg-blue-500 hover:bg-blue-600 text-white shadow-xl shadow-blue-500/30'
                                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {loading && plan.popular ? 'Processing...' : plan.cta}
                                </button>
                            </motion.div>
                        );
                    })}
                </div>

                <div className={`mt-24 grid md:grid-cols-3 gap-12 border-t pt-20 ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
                    <div className="text-center">
                        <div className={`w-16 h-16 rounded-[20px] flex items-center justify-center mx-auto mb-6 shadow-sm ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                            <Clock className="text-blue-500 w-8 h-8" />
                        </div>
                        <h4 className={`font-black uppercase tracking-tight mb-2 ${isDarkMode ? 'text-white' : ''}`}>Cooling Trial</h4>
                        <p className={`text-sm font-medium leading-relaxed ${isDarkMode ? 'text-gray-500' : 'text-slate-500'}`}>Full 60 days of practice to align your internal rhythms without commitment.</p>
                    </div>
                    <div className="text-center">
                        <div className={`w-16 h-16 rounded-[20px] flex items-center justify-center mx-auto mb-6 shadow-sm ${isDarkMode ? 'bg-orange-500/10' : 'bg-orange-50'}`}>
                            <Sun className="text-orange-500 w-8 h-8" />
                        </div>
                        <h4 className={`font-black uppercase tracking-tight mb-2 ${isDarkMode ? 'text-white' : ''}`}>Global Access</h4>
                        <p className={`text-sm font-medium leading-relaxed ${isDarkMode ? 'text-gray-500' : 'text-slate-500'}`}>Fair regional pricing ensuring the ancient wisdom is accessible to everyone.</p>
                    </div>
                    <div className="text-center">
                        <div className={`w-16 h-16 rounded-[20px] flex items-center justify-center mx-auto mb-6 shadow-sm ${isDarkMode ? 'bg-white/5' : 'bg-slate-900'}`}>
                            <Shield className={`${isDarkMode ? 'text-blue-500' : 'text-white'} w-8 h-8`} />
                        </div>
                        <h4 className={`font-black uppercase tracking-tight mb-2 ${isDarkMode ? 'text-white' : ''}`}>Sacred Security</h4>
                        <p className={`text-sm font-medium leading-relaxed ${isDarkMode ? 'text-gray-500' : 'text-slate-500'}`}>Your data and daily logs are encrypted with the highest modern standards.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingPage;
